import { Directive, HostListener, inject, Input } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

/**
 * Router link helper that navigates relative to the current route by default.
 *
 * - `Ctrl/Cmd + click` opens the target in a new tab using the calculated URL.
 * - When `relativeTo` is true (default), navigation is resolved against `ActivatedRoute`.
 * - `queryParams` are appended in both navigation and new-tab modes.
 *
 * Inputs:
 * - `localRouterLink: string` — Target commands/URL segment.
 * - `relativeTo?: boolean` — Resolve against current route, defaults to true.
 * - `queryParams?: Record<string, string>` — Query params to merge.
 *
 * Example:
 * ```html
 * <a localRouterLink="details" [queryParams]="{ id: item.id }">Open</a>
 * ```
 */
@Directive({
    selector: '[localRouterLink]',
    standalone: true,
})
export class LocalRouterLinkDirective {
    private router = inject(Router);

    private route = inject(ActivatedRoute);

    @HostListener('click', ['$event']) onKeydownHandler(event) {
        if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            const currentUrl = window.location.pathname;
            const url = this.relativeTo
                ? this.router.serializeUrl(
                      this.router.createUrlTree([currentUrl, this.localRouterLink]),
                  )
                : this.localRouterLink;
            const searchParams = this.queryParams
                ? '?' + new URLSearchParams(this.queryParams).toString()
                : '';

            // console.log('LocalRouterLinkDirective', url, searchParams);
            window.open(url + searchParams, '_blank');
        } else {
            this.navigate();
        }
    }

    /**
     * Route segment or URL to navigate to.
     */
    @Input() localRouterLink: string;

    /**
     * Resolve navigation relative to current route. Defaults to true.
     */
    @Input() relativeTo?: boolean = true;

    /**
     * Query parameters to append to the URL.
     */
    @Input() queryParams?;

    /**
     * Performs navigation based on inputs and current route.
     */
    navigate() {
        this.router.navigate([this.localRouterLink], {
            relativeTo: this.relativeTo ? this.route : null,
            queryParams: {
                ...this.queryParams,
            },
        });
    }
}
